import Axios from '../axios';
import { createContext, useContext, useEffect, useState } from 'react';
import { UserContext } from './userContext';
import { determineDateRelation, formatDate } from '../utils/versionHistory/date';
import { useDispatch, useSelector } from 'react-redux';
import * as actionTypes from "../store/Actions/actions";

const VersionHistoryContext = createContext();

const VersionHistoryProvider = ({ children }) => {
    const dispatch = useDispatch();
    const canvas = useSelector(state => state.present)
    const { flimpID, userID, sessionID } = useContext(UserContext);
    const [showHistory, setShowHistory] = useState(false);
    const [currentCanvas, setCurrentCanvas] = useState(null)
    const [selectedVersion, setSelectedVersion] = useState(null)
    const [versions, setVersions] = useState({});
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const updateCanvas = (data) => {
        dispatch(actionTypes.title(data.title));
        dispatch(actionTypes.description(data.description));
        dispatch(actionTypes.infoMeta_title(data.meta_title));
        dispatch(actionTypes.canvas(data));
        if (!data.anchorNavigation)
            dispatch(actionTypes.set_anchor_navigation({
                "enable": false,
                "sticky": false,
                "page": data.layoutTitle,
                "tabs": []
            }))
    }

    const fetchVersionData = async (versionID) => {
        const res = await Axios.VersionHistoryService.GET_FLIMP_VERSION(versionID);
        return res.data?.data?.flimp_data;
    }

    const getVersionsData = () => {
        setIsLoading(true)
        Axios.VersionHistoryService.GET_FLIMP_VERSIONS(flimpID)
            .then(async res => {
                const groups = res.data?.data ?? [];
                if (groups.length) {
                    let index = 0;
                    const groupedVersions = groups.map(group => {
                        return group.map(v => {
                            const isoString = v.updated_at.replace(" ", "T") + "Z";
                            const date = new Date(isoString);
                            if (date && isNaN(date.getTime())) throw new Error("wrong date")
                            const formatedDate = formatDate(date);
                            const dateCategory = determineDateRelation(date);
                            index = index + 1;
                            return {
                                ...v,
                                version_id: v.id,
                                version_date: v.updated_at,
                                formatedDate,
                                dateCategory,
                                versionNumber: index
                            }
                        })
                    });

                    const groupByCategory = (items, key) => {
                        return items.reduce((accumulator, item) => {
                            if (!accumulator[item[0][key]]) {
                                accumulator[item[0][key]] = [];
                            }
                            accumulator[item[0][key]].push(item);
                            return accumulator;
                        }, {});
                    }
                    setTotal(index);
                    const versionsByCategory = groupByCategory(groupedVersions, 'dateCategory')
                    setVersions(versionsByCategory);

                    const firstMeta = groupedVersions[0][0];
                    const firstData = await fetchVersionData(firstMeta.version_id);
                    const firstVersion = { ...firstMeta, ...firstData };
                    setSelectedVersion(firstVersion);
                    updateCanvas(firstVersion);
                }
            })
            .catch(err => {
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    useEffect(() => {
        if (showHistory && flimpID) getVersionsData();
    }, [showHistory])


    const openVersionHistory = () => {
        setCurrentCanvas(canvas);
        setShowHistory(true);
    }

    const closeVersionHistory = () => {
        setShowHistory(false);
        if (currentCanvas) {
            updateCanvas(currentCanvas);
        }
    }

    const handleDuplicate = async (title) => {
        const data = {
            t: title,
            id: selectedVersion.version_id,
            f: flimpID,
            u: userID,
            sn: sessionID
        };
        const formBody = Object.keys(data).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`).join('&');
        const res = await Axios.VersionHistoryService.CLONE_FLIMP_VERSION(formBody);
        if (res.status === 200 && res.data.status === "success") {
            const newFlimpID = res.data.data.flimp_id;
            window.open("?u=" + userID + "&f=" + newFlimpID + "&sn=" + sessionID, '_blank').focus();
            setTimeout(() => {
                closeVersionHistory();
            }, 1000);
            return res;
        } else {
            throw new Error(res.data.msg)
        }
    }

    const handleRestore = async () => {
        const data = {
            fd: JSON.stringify(canvas),
            f: flimpID,
            u: userID,
            sn: sessionID,
        };

        const res = await Axios.MicrositeService.POST_MICROSITE(data);
        if (res.status === 200 && res.data.statusCode === "success") {
            setCurrentCanvas(canvas);
            setTimeout(() => {
                setShowHistory(false);
            }, 1000);
        } else if (res.status === 200 && res.data.statusCode === "error") {
            throw new Error(res.data.message)
        }
    }

    const handleGetHistory = () => {
        return versions;
    }

    const handleSelectVersion = async (category, groupID, versionID) => {
        const meta = versions[category][groupID].find(v => v.version_id === versionID)
        if (!meta) return;
        try {
            const data = await fetchVersionData(versionID);
            const fullVersion = { ...meta, ...data };
            setSelectedVersion(fullVersion);
            updateCanvas(fullVersion);
        } catch (err) {
        }
    }

    const state = {
        selectedVersion,
        versions,
        showHistory,
        total,
        isLoading
    }

    const actions = {
        loadVersionsData: getVersionsData,
        duplicate: handleDuplicate,
        restore: handleRestore,
        getHistory: handleGetHistory,
        openVersionHistory: openVersionHistory,
        closeVersionHistory: closeVersionHistory,
        selectVersion: handleSelectVersion
    }

    return (
        <VersionHistoryContext.Provider value={{ state, actions }}>
            {children}
        </VersionHistoryContext.Provider>
    );
};

export { VersionHistoryContext, VersionHistoryProvider };